<template>
  <div class="admin-users">
    <div class="page-header">
      <h2>{{ $t('legacy.Управление Пользователями') }}</h2>
      <div class="search-box">
        <input 
          v-model="searchQuery" 
          type="text" 
          :placeholder="$t('legacy.Поиск по email или имени...')"
          class="search-input"
        />
      </div>
    </div>

    <div class="users-table">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Email</th>
            <th>{{ $t('legacy.Имя') }}</th>
            <th>{{ $t('legacy.Роль') }}</th>
            <th>{{ $t('legacy.Подписка') }}</th>
            <th>{{ $t('legacy.Кредиты') }}</th>
            <th>{{ $t('legacy.Автоплатёж') }}</th>
            <th>{{ $t('legacy.Статус') }}</th>
            <th>{{ $t('legacy.Дата регистрации') }}</th>
            <th>{{ $t('legacy.Действия') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.id">
            <td>{{ user.id }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.name || '-' }}</td>
            <td>
              <span :class="['role-badge', user.role]">
                {{ user.role === 'admin' ? $t('legacy.Администратор') : $t('legacy.Пользователь') }}
              </span>
            </td>
            <td>
              <span :class="['subscription-badge', getSubscriptionClass(user)]">
                {{ getSubscriptionLabel(user) }}
              </span>
              <span v-if="user.subscription_end_date && ['paid','base','premium'].includes(user.subscription_type)" class="subscription-date">
                {{ $t('legacy.до') }} {{ formatDate(user.subscription_end_date) }}
              </span>
            </td>
            <td>{{ user.ai_credits || 0 }}</td>
            <td>
              <div class="autopay-cell">
                <span v-if="user.yookassa_payment_method_id" class="autopay-method">
                  {{ methodIcon(user.yookassa_payment_method_type) }}
                  {{ formatMethodType(user.yookassa_payment_method_type) }}
                </span>
                <span v-else class="autopay-method autopay-none">—</span>
                <span :class="['autopay-badge', user.subscription_auto_renew ? 'autopay-on' : 'autopay-off']">
                  {{ user.subscription_auto_renew ? $t('legacy.вкл') : $t('legacy.выкл') }}
                </span>
              </div>
            </td>
            <td>
              <span :class="['status-badge', user.is_blocked ? 'blocked' : 'active']">
                {{ user.is_blocked ? $t('legacy.Заблокирован') : $t('legacy.Активен') }}
              </span>
            </td>
            <td>{{ formatDate(user.created_at) }}</td>
            <td>
              <div class="actions">
                <button @click="editUser(user)" class="btn-icon btn-edit" :title="$t('legacy.Редактировать')">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button v-if="user.role !== 'admin'" @click="loginAsUser(user)" class="btn-icon btn-login-as" :title="$t('legacy.Войти в аккаунт')">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                </button>
                <button v-if="!user.is_blocked" @click="blockUser(user)" class="btn-icon btn-block" :title="$t('legacy.Заблокировать')">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </button>
                <button v-else @click="unblockUser(user)" class="btn-icon btn-unblock" :title="$t('legacy.Разблокировать')">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                </button>
                <button @click="deleteUser(user)" class="btn-icon btn-delete" :disabled="user.id === currentUserId" :title="$t('common.delete')">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editingUser" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content modal-wide">
        <h3>{{ $t('legacy.Редактировать Пользователя') }}</h3>
        <form @submit.prevent="saveUser">
          <div class="form-grid">
            <div class="form-column">
              <div class="form-group">
                <label>Email</label>
                <input v-model="form.email" type="email" required />
              </div>

              <div class="form-group">
                <label>{{ $t('legacy.Имя') }}</label>
                <input v-model="form.name" type="text" />
              </div>

              <div class="form-group">
                <label>{{ $t('legacy.Роль') }}</label>
                <select v-model="form.role">
                  <option value="user">{{ $t('legacy.Пользователь') }}</option>
                  <option value="admin">{{ $t('legacy.Администратор') }}</option>
                </select>
              </div>

              <div class="form-group">
                <label>{{ $t('legacy.AI кредиты') }}</label>
                <input v-model="form.ai_credits" type="number" min="0" />
              </div>
            </div>

            <div class="form-column">
              <div class="form-section">
                <h4>{{ $t('legacy.Управление подпиской') }}</h4>
                
                <div class="form-group">
                  <label>{{ $t('legacy.Тариф') }}</label>
                  <select v-model="form.subscription_plan">
                    <option value="">{{ $t('legacy.Бесплатный (Free)') }}</option>
                    <option value="base">{{ $t('legacy.Базовый (Base)') }}</option>
                    <option value="premium">{{ $t('legacy.Премиум (Premium)') }}</option>
                  </select>
                </div>

                <div class="form-group" v-if="form.subscription_plan">
                  <label>{{ $t('legacy.Действует до') }}</label>
                  <input v-model="form.subscription_end_date" type="date" />
                </div>
              </div>

              <div class="form-section autopay-section">
                <h4>{{ $t('legacy.Автоплатёж') }}</h4>
                <div class="info-row">
                  <span class="info-label">{{ $t('legacy.Статус') }}</span>
                  <span class="info-value" :class="editingUser.yookassa_payment_method_id ? 'badge-yes' : 'badge-no'">
                    {{ editingUser.yookassa_payment_method_id ? $t('legacy.Привязан') : $t('legacy.Не привязан') }}
                  </span>
                </div>
                <div class="info-row" v-if="editingUser.yookassa_payment_method_type">
                  <span class="info-label">{{ $t('legacy.Способ оплаты') }}</span>
                  <span class="info-value">
                    {{ methodIcon(editingUser.yookassa_payment_method_type) }}
                    {{ formatMethodType(editingUser.yookassa_payment_method_type) }}
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('legacy.Автопродление') }}</span>
                  <span class="info-value" :class="editingUser.subscription_auto_renew ? 'badge-yes' : 'badge-no'">
                    {{ editingUser.subscription_auto_renew ? $t('legacy.Включено') : $t('legacy.Отключено') }}
                  </span>
                </div>
                <div class="info-row" v-if="editingUser.yookassa_payment_method_id">
                  <span class="info-label">{{ $t('legacy.ID метода') }}</span>
                  <span class="info-value method-id">{{ editingUser.yookassa_payment_method_id }}</span>
                </div>
                <div class="info-row" v-if="editingUser.yookassa_payment_method_id">
                  <button
                    type="button"
                    class="btn-charge-renewal"
                    :disabled="chargeLoading"
                    @click="chargeRenewal(editingUser)"
                  >
                    {{ chargeLoading ? $t('legacy.Списание...') : $t('legacy.Списать вручную') }}
                  </button>
                </div>
              </div>

              <div class="form-section ambassador-section">
                <h4>{{ $t('legacy.Партнерская программа') }}</h4>
                
                <div class="form-group checkbox-group">
                  <label class="checkbox-label">
                    <input type="checkbox" v-model="form.is_ambassador" />
                    <span>{{ $t('legacy.Амбасадор') }}</span>
                  </label>
                </div>

                <template v-if="form.is_ambassador">
                  <div class="form-group">
                    <label>{{ $t('legacy.Процент комиссии') }}</label>
                    <input v-model.number="form.commission_percent" type="number" min="0" max="100" step="0.01" />
                  </div>
                  <div class="form-group">
                    <label>{{ $t('legacy.Лимит платежей') }}</label>
                    <input v-model.number="form.commission_payments_limit" type="number" min="1" max="999" />
                    <small>{{ $t('legacy.Сколько платежей реферала приносят комиссию') }}</small>
                  </div>
                  <div class="form-group checkbox-group">
                    <label class="checkbox-label">
                      <input type="checkbox" v-model="form.ambassador_is_active" />
                      <span>{{ $t('legacy.Активен') }}</span>
                    </label>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';
import { ambassadorsApi } from '@/api/ambassadors';
import { getSubscriptionPlans } from '@/api/subscription';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminUsers',
  data() {
    return {
      toast: null,
      users: [],
      subscriptionPlans: [],
      searchQuery: '',
      editingUser: null,
      ambassadorData: null,
      ambassadorLoadError: false,
      chargeLoading: false,
      form: {
        email: '',
        name: '',
        role: 'user',
        subscription_plan: '',
        subscription_end_date: '',
        ai_credits: 0,
        is_ambassador: false,
        commission_percent: 10,
        commission_payments_limit: 1,
        ambassador_is_active: true
      }
    }
  },
  computed: {
    currentUserId() {
      return this.$store.getters['auth/user']?.id;
    },
    filteredUsers() {
      if (!this.users || !Array.isArray(this.users)) return [];
      if (!this.searchQuery) return this.users;
      
      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user => 
        user.email.toLowerCase().includes(query) ||
        (user.name && user.name.toLowerCase().includes(query))
      );
    }
  },
  async created() {
    this.toast = useToast();
    await Promise.all([this.loadUsers(), this.loadPlans()]);
  },
  watch: {
    'form.subscription_plan'(newPlan, oldPlan) {
      if (newPlan && newPlan !== oldPlan) {
        const plan = this.subscriptionPlans.find(p => p.id === newPlan);
        if (plan) {
          this.form.ai_credits = plan.aiCredits;
        }
      }
    }
  },
  methods: {
    async loadPlans() {
      try {
        this.subscriptionPlans = await getSubscriptionPlans();
      } catch (error) {
        console.error('Error loading subscription plans:', error);
      }
    },
    async loadUsers() {
      try {
        const response = await adminApi.getUsers();
        this.users = response.data.users || [];
      } catch (error) {
        console.error('Ошибка загрузки пользователей:', error);
        this.toast.error(this.$t('legacy.Ошибка загрузки пользователей'));
      }
    },

    async editUser(user) {
      this.editingUser = user;
      this.ambassadorData = null;
      this.ambassadorLoadError = false;
      
      try {
        const response = await ambassadorsApi.getByUserId(user.id);
        if (response.is_ambassador && response.ambassador) {
          this.ambassadorData = response.ambassador;
        }
      } catch (error) {
        console.error('Error loading ambassador data:', error);
      }
      
      this.form = {
        email: user.email,
        name: user.name || '',
        role: user.role,
        subscription_plan: ['base', 'premium'].includes(user.subscription_plan)
          ? user.subscription_plan
          : (user.subscription_type === 'base' ? 'base'
            : (user.subscription_type === 'premium' || user.subscription_type === 'paid') ? 'premium'
            : ''),
        subscription_end_date: user.subscription_end_date ? this.formatDateForInput(user.subscription_end_date) : '',
        ai_credits: user.ai_credits || 0,
        is_ambassador: !!this.ambassadorData,
        commission_percent: this.ambassadorData?.commission_percent || 10,
        commission_payments_limit: this.ambassadorData?.commission_payments_limit || 1,
        ambassador_is_active: this.ambassadorData?.is_active ?? true
      };
    },

    async saveUser() {
      try {
        const data = {
          email: this.form.email,
          name: this.form.name,
          role: this.form.role,
          subscription_plan: this.form.subscription_plan,
          subscription_end_date: this.form.subscription_end_date,
          ai_credits: this.form.ai_credits
        };
        
        if (data.subscription_plan === 'base') {
          data.subscription_type = 'base';
        } else if (data.subscription_plan === 'premium') {
          data.subscription_type = 'premium';
        } else if (data.subscription_plan) {
          data.subscription_type = 'paid';
        } else {
          data.subscription_type = 'free';
          data.subscription_end_date = null;
        }
        await adminApi.updateUser(this.editingUser.id, data);
        
        if (this.form.is_ambassador) {
          if (this.ambassadorData) {
            await ambassadorsApi.update(this.ambassadorData.id, {
              commission_percent: this.form.commission_percent,
              commission_payments_limit: this.form.commission_payments_limit,
              is_active: this.form.ambassador_is_active
            });
          } else {
            await ambassadorsApi.create({
              userId: this.editingUser.id,
              commissionPercent: this.form.commission_percent,
              commissionPaymentsLimit: this.form.commission_payments_limit
            });
          }
        } else if (this.ambassadorData) {
          await ambassadorsApi.delete(this.ambassadorData.id);
        }
        
        this.toast.success(this.$t('legacy.Пользователь обновлен'));
        await this.loadUsers();
        this.closeModal();
      } catch (error) {
        console.error('Error saving user:', error);
        this.toast.error(error.response?.data?.error || this.$t('legacy.Ошибка сохранения'));
      }
    },

    async blockUser(user) {
       if (!confirm(this.$t('Заблокировать пользователя "{email}"?', { email: user.email }))) return;

      try {
        await adminApi.updateUser(user.id, { is_blocked: true });
        this.toast.success(this.$t('legacy.Пользователь заблокирован'));
        await this.loadUsers();
      } catch (error) {
        this.toast.error(this.$t('legacy.Ошибка блокировки'));
      }
    },

    async unblockUser(user) {
      try {
        await adminApi.updateUser(user.id, { is_blocked: false });
        this.toast.success(this.$t('legacy.Пользователь разблокирован'));
        await this.loadUsers();
      } catch (error) {
        this.toast.error(this.$t('legacy.Ошибка разблокировки'));
      }
    },

    async deleteUser(user) {
      if (user.id === this.currentUserId) {
        this.toast.error(this.$t('legacy.Нельзя удалить себя'));
        return;
      }

       if (!confirm(this.$t('legacy.dynamic.userDelete', { email: user.email }))) return;

      try {
        await adminApi.deleteUser(user.id);
        this.toast.success(this.$t('legacy.Пользователь удален'));
        await this.loadUsers();
      } catch (error) {
        this.toast.error(this.$t('errors.delete'));
      }
    },

    closeModal() {
      this.editingUser = null;
      this.ambassadorData = null;
      this.ambassadorLoadError = false;
      this.form = { 
        email: '', 
        name: '', 
        role: 'user', 
        subscription_plan: '', 
        subscription_end_date: '', 
        ai_credits: 0,
        is_ambassador: false,
        commission_percent: 10,
        commission_payments_limit: 1,
        ambassador_is_active: true
      };
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return this.$formatDate(date);
    },
    
    formatDateForInput(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    },
    getSubscriptionClass(user) {
      if (user.role === 'admin') return 'admin';
      const paidTypes = ['paid', 'base', 'premium'];
      if (paidTypes.includes(user.subscription_type) && user.subscription_end_date) {
        const endDate = new Date(user.subscription_end_date);
        if (endDate > new Date()) return 'paid';
        return 'expired';
      }
      return 'free';
    },
    getSubscriptionLabel(user) {
      if (user.role === 'admin') return this.$t('legacy.Админ');
      const paidTypes = ['paid', 'base', 'premium'];
      if (paidTypes.includes(user.subscription_type) && user.subscription_end_date) {
        const endDate = new Date(user.subscription_end_date);
        const planName = (user.subscription_type === 'premium' || user.subscription_type === 'paid')
          ? 'Premium' : 'Base';
        if (endDate > new Date()) return planName;
        return `${planName} (${this.$t('legacy.истекла')})`;
      }
      return 'Free';
    },
    methodIcon(type) {
      const map = {
        bank_card: '💳',
        sbp: '⚡',
        sberbank: '🟢',
        tinkoff_bank: '🟡',
        yoo_money: '💜',
      };
      return map[type] || '💰';
    },
    formatMethodType(type) {
      if (!type) return '—';
      const map = {
        bank_card: this.$t('legacy.Банковская карта'),
        sbp: this.$t('legacy.СБП'),
        sberbank: 'SberPay',
        tinkoff_bank: 'T-Pay',
        yoo_money: this.$t('legacy.ЮMoney'),
        cash: this.$t('legacy.Наличные')
      };
      return map[type] || type;
    },

    async loginAsUser(user) {
       if (!confirm(this.$t('Войти в аккаунт {email}?\n\nВы сможете вернуться обратно через баннер.', { email: user.email }))) return;
      try {
        const response = await adminApi.impersonate(user.id);
        const { token, user: targetUser } = response.data;

        const adminToken = localStorage.getItem('userToken');
        const adminUserRaw = localStorage.getItem('user');
        localStorage.setItem('adminOriginalToken', adminToken);
        localStorage.setItem('adminOriginalUser', adminUserRaw);
        localStorage.setItem('impersonating', JSON.stringify({ id: targetUser.id, email: targetUser.email, name: targetUser.name }));

        localStorage.setItem('userToken', token);
        localStorage.setItem('user', JSON.stringify(targetUser));
        localStorage.setItem('isAuthenticated', 'true');

        window.location.href = '/projects';
      } catch (error) {
        const errMsg = error.response?.data?.error || this.$t('legacy.Ошибка при входе в аккаунт');
        this.toast.error(errMsg);
      }
    },

    async chargeRenewal(user) {
      const methodLabel = this.formatMethodType(user.yookassa_payment_method_type);
       if (!confirm(this.$t('Инициировать списание для пользователя {email} ({method})?', { email: user.email, method: methodLabel }))) return;

      this.chargeLoading = true;
      try {
        const response = await adminApi.chargeRenewal(user.id);
        const { success, message } = response.data;
        if (success) {
          this.toast.success(message || this.$t('legacy.Платёж инициирован'));
        } else {
          this.toast.warning(message || this.$t('legacy.Платёж не прошёл — проверьте логи'));
        }
        await this.loadUsers();
      } catch (error) {
        const errMsg = error.response?.data?.error || this.$t('legacy.Ошибка при попытке списания');
        this.toast.error(errMsg);
      } finally {
        this.chargeLoading = false;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.admin-users {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    h2 {
      margin: 0;
    }

    .search-box {
      flex: 1;
      max-width: 400px;
      margin-left: 40px;

      .search-input {
        width: 100%;
        padding: 10px 16px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
    }
  }

  .users-table {
    background: white;
    border-radius: 8px;
    overflow-x: auto;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;

      th, td {
        padding: 10px 8px;
        text-align: left;
        border-bottom: 1px solid #eee;
        white-space: nowrap;
      }

      th {
        background: #f8f9fa;
        font-weight: 600;
        color: #333;
        font-size: 12px;
      }

      tbody tr:hover {
        background: #f8f9fa;
      }

      .role-badge, .status-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
      }

      .role-badge {
        &.admin {
          background: #ff9800;
          color: white;
        }

        &.user {
          background: #e3f2fd;
          color: #1976d2;
        }
      }

      .status-badge {
        &.active {
          background: #e8f5e9;
          color: #388e3c;
        }

        &.blocked {
          background: #ffebee;
          color: #c62828;
        }
      }

      .subscription-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;

        &.free {
          background: #f5f5f5;
          color: #666;
        }

        &.paid {
          background: #fff8e1;
          color: #f57c00;
        }

        &.expired {
          background: #ffebee;
          color: #c62828;
        }

        &.admin {
          background: #e8eaf6;
          color: #3f51b5;
        }
      }

      .subscription-date {
        display: block;
        font-size: 11px;
        color: #999;
        margin-top: 2px;
      }

      .autopay-cell {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .autopay-method {
          font-size: 12px;
          color: #333;

          &.autopay-none {
            color: #bbb;
          }
        }

        .autopay-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
          width: fit-content;

          &.autopay-on {
            background: #e8f5e9;
            color: #388e3c;
          }

          &.autopay-off {
            background: #f5f5f5;
            color: #999;
          }
        }
      }

      .actions {
        display: flex;
        gap: 4px;
        flex-wrap: nowrap;
        align-items: center;

        .btn-icon {
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: opacity 0.15s, background 0.15s;

          &:disabled {
            opacity: 0.35;
            cursor: not-allowed;
          }

          &:hover:not(:disabled) {
            opacity: 0.82;
          }

          &.btn-edit {
            background: #28a745;
            color: white;
          }

          &.btn-login-as {
            background: #4a6fa5;
            color: white;
          }

          &.btn-block {
            background: #ffc107;
            color: #555;
          }

          &.btn-unblock {
            background: #17a2b8;
            color: white;
          }

          &.btn-delete {
            background: #dc3545;
            color: white;
          }
        }
      }
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    padding: 30px;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;

    h3 {
      margin-top: 0;
    }
    
    &.modal-wide {
      max-width: 700px;
    }
    
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      
      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }
    }
    
    .form-column {
      .form-section:first-child {
        margin-top: 0;
        padding-top: 0;
        border-top: none;
      }
    }

    .form-group {
      margin-bottom: 20px;

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
      }

      input, select {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
    }
    
    .form-section {
      margin-top: 24px;
      padding-top: 20px;
      border-top: 1px solid #eee;
      
      h4 {
        margin: 0 0 16px 0;
        font-size: 16px;
        color: #333;
      }
      
      small {
        display: block;
        margin-top: 4px;
        color: #666;
        font-size: 12px;
      }
    }
    
    .checkbox-group {
      .checkbox-label {
        display: flex;
        align-items: center;
        cursor: pointer;
        
        input[type="checkbox"] {
          width: auto;
          margin-right: 8px;
          cursor: pointer;
        }
        
        span {
          font-weight: normal;
        }
      }
    }
    
    .autopay-section {
      background: #f0f8ff;
      padding: 16px;
      border-radius: 8px;
      margin-top: 20px;
      border: 1px solid #cce4f0;

      h4 {
        color: #496782;
      }

      .btn-charge-renewal {
        width: 100%;
        padding: 8px 16px;
        background: #496782;
        color: #fff;
        border: none;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;

        &:hover:not(:disabled) {
          background: #26374E;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }

    .info-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 13px;

      .info-label {
        color: #666;
      }

      .info-value {
        color: #26374E;
        font-weight: 500;
      }

      .method-id {
        font-size: 11px;
        color: #888;
        font-family: monospace;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .badge-yes {
        background: #d4edda;
        color: #155724;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 600;
      }

      .badge-no {
        background: #f8d7da;
        color: #721c24;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 600;
      }
    }

    .ambassador-section {
      background: #f8f9fa;
      padding: 16px;
      border-radius: 8px;
      margin-top: 20px;
      border: 1px solid #e9ecef;
    }
    
    .error-message {
      color: #dc3545;
      font-size: 14px;
      padding: 10px;
      background: #f8d7da;
      border-radius: 4px;
      border: 1px solid #f5c6cb;
    }

    .form-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;

      button {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;

        &.btn-cancel {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }

        &.btn-primary {
          background: #007bff;
          color: white;

          &:hover {
            background: #0056b3;
          }
        }
      }
    }
  }
}
</style>
